import { Guid } from '../../../store/model/guid';
import { GetItemById, getItemByIdSelector } from '../../../store/reducers/items/itemsSelectors';
import { AssertionException } from '../../../utils/log';
import { CompletedItemsStore, createCompletedItemsStoreFromState } from '../models/CompletedItemsStore';
import {
  completedItem,
  isCompleted,
  ItemsCompletion,
  itemsCompletionAddComplete,
  itemsCompletionAddIncomplete,
  notMandatoryItem,
  unopenedItem,
} from '../models/ItemsCompletion';
import { SequencedFlowContentEvaluator } from './SequencedFlowEvaluator/SequencedFlowContentEvaluator';
import {
  BlindMapQuestion,
  HotspotTab,
  isBlindMapQuestion,
  isHotspot,
  isHotspotTab,
  isNonSequencedFlowItem,
  isQuestion,
  isSequencedFlowStep,
  Item,
  Question,
  Section,
  SequencedFlowItem,
  SequencedFlowStep,
} from '@ael/core-api';
import * as Immutable from 'immutable';
import { createSelector } from 'reselect';

export class UserProgressService {
  private _completedItemsStore: CompletedItemsStore;
  private _getItemById: (id: Guid) => Item;

  constructor(completedItemsStore: CompletedItemsStore, getItemById: (id: Guid) => Item) {
    this._completedItemsStore = completedItemsStore;
    this._getItemById = getItemById;
  }

  /**
   * Returns progress for linear flow only as items that user completed until now, and items required to complete which
   * are calculated as biggest amount of items user can go through from current section, or if linear flow is completed returns 100% by default
   */
  evaluateSequencedFlow = (item: Section | SequencedFlowItem, parent: SequencedFlowItem): number => {
    if (this._completedItemsStore.isFinished(parent)) {
      return 100;
    }

    return this._evaluateSequencedFlowOrSection(item, parent);
  };

  /**
   * Returns progress for Section, calculated as percentage of completed underlying content items.
   * It is not currently precise as there can be cases where some underlying section is completed but it doesnt return 100%
   */
  evaluateSection = (item: Section): number => {
    return this._evaluateSequencedFlowOrSection(item);
  };

  /**
   * Returns progress as items that user completed until now, and items required to complete which
   * are calculated as biggest amount of items user can go through from current section
   */
  private _evaluateSequencedFlowOrSection = (item: Section | SequencedFlowItem, parent?: SequencedFlowItem): number => {
    const progress = new SequencedFlowContentEvaluator(item, this, this._getItemById, parent).run();
    if (progress.itemsCount === 0) {
      return 0;
    }

    const percentage = Math.floor((progress.completedItemsCount * 100) / progress.itemsCount);

    return percentage;
  };

  /**
   * Returns number of child Items completed from all child Items in given item.
   * Usage e.g. for displaying information like '3/8 Hotspots completed'.
   * For this case some item types are not counted in e.g. 'Starting Message'
   * as the message '4/9 Hotspots completed' would not make sense.
   */
  evaluate = (item: Item): ItemsCompletion => {
    // Ends the recursion here as those are leaf nodes
    // Effectively only checks whether content item is inside CompletionStore
    if (isHotspotTab(item) || isQuestion(item) || isBlindMapQuestion(item) || isSequencedFlowStep(item)) {
      return this._evaluateChild(item);
    }
    if (isNonSequencedFlowItem(item)) {
      return this._evaluateChildren(item.hotspots);
    }
    if (isHotspot(item)) {
      return this._evaluateChildren(item.tabs);
    }
    throw AssertionException(`Unexpected type ${item.type}`);
  };

  _evaluateChildren = (children: Immutable.List<Guid>) => {
    return children.reduce((acc: ItemsCompletion, child: Guid): ItemsCompletion => {
      const evaluation = this.evaluate(this._getItemById(child));

      if (isCompleted(evaluation)) {
        return itemsCompletionAddComplete(acc);
      } else {
        return itemsCompletionAddIncomplete(acc);
      }
    }, notMandatoryItem);
  };

  _evaluateChild = (item: HotspotTab | BlindMapQuestion | Question | SequencedFlowStep) => {
    return this._completedItemsStore.isFinished(item) ? completedItem : unopenedItem;
  };
}

export const userProgressServiceSelector = createSelector(
  createCompletedItemsStoreFromState,
  getItemByIdSelector,
  (completedItemsStore: CompletedItemsStore, getItemById: GetItemById): UserProgressService =>
    new UserProgressService(completedItemsStore, getItemById)
);
